import { useState, useRef, useCallback, useEffect } from 'react';
import { FaCamera, FaRedo, FaCheck, FaTimes } from 'react-icons/fa';
import './PhotoCapture.scss';

interface PhotoCaptureProps {
    onCapture: (base64: string) => void;
    onCancel?: () => void;
    label?: string;
    required?: boolean;
}

const MIN_CAPTURE_DIMENSION = 16;

const PhotoCapture = ({ onCapture, onCancel, label = 'Take Photo', required = false }: PhotoCaptureProps) => {
    const [preview, setPreview] = useState<string | null>(null);
    const [cameraActive, setCameraActive] = useState(false);
    const [videoReady, setVideoReady] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const videoRef = useRef<HTMLVideoElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const streamRef = useRef<MediaStream | null>(null);

    const stopStream = useCallback(() => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(t => t.stop());
            streamRef.current = null;
        }
        setCameraActive(false);
        setVideoReady(false);
    }, []);

    useEffect(() => {
        return () => { stopStream(); };
    }, [stopStream]);

    // Connect stream to video element once both are available.
    // The video element only mounts after setCameraActive(true), so we
    // must wait for a render cycle before assigning srcObject.
    useEffect(() => {
        const video = videoRef.current;
        const stream = streamRef.current;
        if (!cameraActive || !video || !stream) return;

        video.srcObject = stream;
        video.play().catch((err) => {
            console.error('[PhotoCapture] video.play() failed:', err);
            setError('Could not start camera preview.');
            stopStream();
        });
    }, [cameraActive, stopStream]);

    const startCamera = useCallback(async () => {
        setError(null);
        setVideoReady(false);
        try {
            // Check permission state first to avoid repeated prompts on iOS
            if (navigator.permissions && navigator.permissions.query) {
                try {
                    const result = await navigator.permissions.query({ name: 'camera' as PermissionName });
                    if (result.state === 'denied') {
                        setError('Camera access denied. Please allow camera access in your browser settings.');
                        return;
                    }
                } catch {
                    // permissions.query may not support 'camera' on all browsers — fall through
                }
            }
            const stream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } }
            });
            streamRef.current = stream;
            setCameraActive(true);
        } catch {
            setError('Camera not available. Please check permissions.');
        }
    }, []);

    const handleVideoPlaying = useCallback(() => {
        setVideoReady(true);
    }, []);

    const capturePhoto = useCallback(() => {
        if (!videoRef.current || !canvasRef.current) return;
        const video = videoRef.current;
        const canvas = canvasRef.current;

        if (video.videoWidth < MIN_CAPTURE_DIMENSION || video.videoHeight < MIN_CAPTURE_DIMENSION) {
            setError('Camera feed not ready. Please wait a moment and try again.');
            return;
        }

        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        ctx.drawImage(video, 0, 0);
        const base64 = canvas.toDataURL('image/jpeg', 0.8);
        setPreview(base64);
        stopStream();
    }, [stopStream]);

    const retake = useCallback(() => {
        setPreview(null);
        startCamera();
    }, [startCamera]);

    const confirm = useCallback(() => {
        if (preview) {
            onCapture(preview);
        }
    }, [preview, onCapture]);

    if (preview) {
        return (
            <div className="photo-capture">
                <div className="photo-preview">
                    <img src={preview} alt="Captured proof" />
                    <div className="preview-actions">
                        <button className="btn-retake" onClick={retake}>
                            <FaRedo /> Retake
                        </button>
                        <button className="btn-confirm-photo" onClick={confirm}>
                            <FaCheck /> Use Photo
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (cameraActive) {
        return (
            <div className="photo-capture camera-active">
                <video
                    ref={videoRef}
                    playsInline
                    muted
                    className="camera-viewfinder"
                    onPlaying={handleVideoPlaying}
                />
                <canvas ref={canvasRef} style={{ display: 'none' }} />
                <div className="camera-controls">
                    <button className="btn-cancel-camera" onClick={() => { stopStream(); onCancel?.(); }}>
                        <FaTimes />
                    </button>
                    <button className="btn-capture" onClick={capturePhoto} disabled={!videoReady}>
                        <FaCamera />
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="photo-capture idle">
            {error && <p className="photo-error">{error}</p>}
            <button className="btn-open-camera" onClick={startCamera}>
                <FaCamera />
                <span>{label}{required && <span className="required-star"> *</span>}</span>
            </button>
            {!required && onCancel && (
                <button className="btn-skip-photo" onClick={onCancel}>Skip photo</button>
            )}
        </div>
    );
};

export default PhotoCapture;
